import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/lib/store';
import WalletConnect from '@/components/WalletConnect';
import TelegramLogin from '@/components/TelegramLogin';
import type { TelegramAuthData } from '@/types';

const TELEGRAM_BOT_USERNAME = import.meta.env.VITE_TELEGRAM_BOT_USERNAME || 'LazaiTrader_bot';

export default function LoginPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const [activeTab, setActiveTab] = useState<'wallet' | 'telegram'>('wallet');

  // If already authenticated, redirect to dashboard
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSuccess = () => {
    navigate('/dashboard');
  };

  const handleNewWalletUser = (registrationToken: string, walletAddress: string) => {
    navigate('/register', {
      state: { registrationToken, walletAddress, method: 'wallet' },
    });
  };

  const handleNewTelegramUser = (registrationToken: string, telegramData: TelegramAuthData) => {
    navigate('/register', {
      state: { registrationToken, telegramData, method: 'telegram' },
    });
  };

  return (
    <div className="min-h-screen bg-dark-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 rounded-2xl mb-4">
            <span className="text-2xl font-bold text-white">LT</span>
          </div>
          <h1 className="text-3xl font-bold text-white">LazaiTrader</h1>
          <p className="text-dark-400 mt-2">Automated trading made simple</p>
        </div>

        {/* Login Card */}
        <div className="card">
          <h2 className="text-xl font-semibold text-white mb-6 text-center">Sign In</h2>

          {/* Tabs */}
          <div className="flex mb-6 bg-dark-900 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('wallet')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'wallet'
                  ? 'bg-primary-600 text-white'
                  : 'text-dark-400 hover:text-white'
              }`}
            >
              Wallet
            </button>
            <button
              onClick={() => setActiveTab('telegram')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'telegram'
                  ? 'bg-primary-600 text-white'
                  : 'text-dark-400 hover:text-white'
              }`}
            >
              Telegram
            </button>
          </div>

          {/* Login Methods */}
          <div className="min-h-[160px]">
            {activeTab === 'wallet' ? (
              <WalletConnect
                onSuccess={handleSuccess}
                onNewUser={handleNewWalletUser}
              />
            ) : (
              <TelegramLogin
                botUsername={TELEGRAM_BOT_USERNAME}
                onSuccess={handleSuccess}
                onNewUser={handleNewTelegramUser}
              />
            )}
          </div>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-dark-600" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-2 bg-dark-800 text-dark-400">
                New to LazaiTrader?
              </span>
            </div>
          </div>

          {/* Info */}
          <p className="text-sm text-dark-400 text-center">
            Connect your wallet or Telegram to get started. A smart contract wallet
            will be created for you automatically.
          </p>
        </div>

        {/* Footer */}
        <p className="text-xs text-dark-500 text-center mt-6">
          By signing in, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
}
